/**
 * Sanitize untrusted HTML before render.
 *
 * Every place server-supplied HTML hits the DOM (ticket comment bodies,
 * email replies, customer-portal descriptions) must go through this
 * helper. Never use `dangerouslySetInnerHTML` directly - import from
 * here and wrap.
 *
 * The default DOMPurify config blocks scripts, event handlers,
 * javascript: URLs, and unknown protocols. We extend it slightly to
 * allow target=_blank links (we add rel=noopener noreferrer
 * automatically) so customers can paste hyperlinks into replies.
 */
import DOMPurify from "dompurify";

import { collapseQuoteRegions } from "@/lib/collapseQuotes";

// Force every anchor to open in a new tab and carry the noopener +
// noreferrer rel. Two reasons together:
//   1. Ticket bodies are foreign content (email replies, customer-
//      pasted URLs). When an agent clicks a link inside a ticket, the
//      default same-tab navigation drops them out of the support app
//      and loses their position in the conversation. Forcing
//      target=_blank keeps the ticket open behind the new tab.
//   2. Any new-tab open without ``rel="noopener"`` leaves
//      ``window.opener`` reachable from the opened page, which is the
//      classic reverse-tabnabbing vector. Pinning the rel here is
//      defense-in-depth even though modern browsers default this for
//      cross-origin _blank links.
// Applies uniformly to http(s) / mailto / tel links - the browser
// handles mailto/tel via the OS handler regardless of target.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.nodeType === 1 && (node as Element).tagName === "A") {
    (node as Element).setAttribute("target", "_blank");
    (node as Element).setAttribute("rel", "noopener noreferrer");
  }
});

const SAFE_HTML_CONFIG = {
  ALLOWED_TAGS: [
    "a", "b", "br", "code", "div", "em", "i", "img", "li", "ol",
    "p", "pre", "span", "strong", "u", "ul",
    // Strikethrough. The TipTap strike button emits <s>, markdown
    // ``~~text~~`` renders <s> or <del> depending on the converter -
    // both were silently stripped before 2026-06-11 (text kept, the
    // line-through lost).
    "s", "del",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "blockquote", "hr", "table", "thead", "tbody", "tr", "td", "th",
    // ``colgroup`` + ``col`` are structural-only tags that carry per-
    // column ``width=`` hints. Without them DOMPurify strips the
    // colgroup; the surrounding table then falls back to
    // ``table-layout: fixed`` with no explicit column widths and
    // browsers render every column at 0px. F-104946's Other-Orders
    // table on 2026-05-20 was the canonical breakage: 14 <col
    // width="100"> bytes stripped, table.width:0px inline style kept,
    // result was a 0.56px sliver containing 476 invisible cells.
    "colgroup", "col",
    // <details>/<summary> drive the inline "..." expand affordance
    // collapseQuoteRegions wraps around quoted email threads.
    "details", "summary",
  ],
  ALLOWED_ATTR: [
    "href", "src", "alt", "title", "target",
    "class", "style", "width", "height",
    // ``colspan`` / ``rowspan`` are structural table-cell attributes.
    // Without them a sanitized table that uses cell-merging (common in
    // forwarded-email reports) collapses to a misaligned grid.
    "colspan", "rowspan",
    // Needed so the collapseQuoteRegions wrapper can carry the
    // ``data-testid="quoted-thread"`` hook for tests + dev tools.
    "data-testid",
  ],
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

export interface SanitizeOptions {
  /** Whether to wrap detected email-quote regions in <details>.
   * Default true. Pass false for surfaces where the ENTIRE body is
   * the first message in the thread (e.g. the "Original Ticket"
   * entry at the bottom of the conversation) - those bodies often
   * carry a top-level <blockquote> or Outlook "From:" header that
   * would otherwise collapse the whole message behind the "..."
   * affordance, hiding the original ticket content. */
  collapseQuotes?: boolean;
}

/**
 * Sanitize an HTML string for safe innerHTML rendering.
 *
 * Two-stage pipeline:
 *   1. ``collapseQuoteRegions`` (optional, default ON) wraps any
 *      detected email-quote block in a <details><summary>...
 *      </summary>...</details> element so the conversation thread
 *      shows the new reply by default and stashes the replied-to
 *      history behind an inline "..." button. The transform is
 *      non-destructive: no input ever drops; the browser always
 *      exposes the collapsed content one click away.
 *   2. DOMPurify enforces the tag / attr / URL allowlist.
 *
 * Order matters: collapseQuoteRegions runs FIRST so it can reason
 * about the original wrapper classes (gmail_quote, WordSection,
 * OutlookMessageHeader, ...) before DOMPurify normalizes whitespace
 * or rewrites attributes. DOMPurify then validates the <details> +
 * <summary> markup the collapser injected.
 *
 * @param html - the untrusted HTML string
 * @param options - opt-out flags for the optional transforms
 * @returns sanitized HTML, safe to pass to dangerouslySetInnerHTML
 */
export function sanitizeHtml(
  html: string,
  options: SanitizeOptions = {},
): string {
  const input = options.collapseQuotes === false
    ? html
    : collapseQuoteRegions(html);
  return DOMPurify.sanitize(input, SAFE_HTML_CONFIG);
}

/**
 * Sanitize and return a React-ready `{ __html }` object for direct
 * binding to `dangerouslySetInnerHTML`. Forwards options to
 * ``sanitizeHtml``.
 */
export function safeHtmlProps(
  html: string,
  options: SanitizeOptions = {},
): { __html: string } {
  return { __html: sanitizeHtml(html, options) };
}
